// Area: ai · change sets (owner platform-agent, owner feedback F16).
//
// A change set is what the one platform agent proposes for a task that touches several things: an
// ordered plan of typed tool calls that a member approves once and can take back in one step. The
// Copilot proposes it through the `plan.apply` tool (an approval interrupt); MCP clients queue it and
// a member approves it in the app. These are the shared shapes and the endpoints to list, read,
// approve, reject and undo change sets.
import { z } from 'zod';
import { defineContract } from '../../define';
import { q } from '../../query';

/** Limits a single change set is checked against (workspace settings may lower them). */
export const AI_CHANGE_SET_LIMITS = { maxSteps: 40, maxRecords: 1000, perHour: 30 } as const;

export const ChangeSetStatus = z.enum([
  /** Waiting for a member's approval (MCP clients queue here; the Copilot's plan waits in its run). */
  'pending',
  'applying',
  'applied',
  /** Nothing was applied: a step failed and the whole set rolled back. */
  'failed',
  'rejected',
  'undone',
  /** Undo took back part of the records; what somebody else changed since stays as it is and the rest can be undone again. */
  'partly_undone',
  /** Undo was tried and could not finish: nothing was reverted (see the undo report). */
  'undo_failed',
  'expired',
]);
export type ChangeSetStatus = z.infer<typeof ChangeSetStatus>;

export const ChangeSetOrigin = z.enum(['copilot', 'mcp', 'api']);
export type ChangeSetOrigin = z.infer<typeof ChangeSetOrigin>;

/** What undoing a step takes: its own revert, the set's record operation, or nothing (irreversible). */
export const ChangeSetUndoKind = z.enum(['payload', 'operation', 'none']);

/** One typed tool call of a plan, as the member reviews it. */
export const ChangeSetStepDTO = z.object({
  id: z.string(),
  tool: z.string(),
  title: z.string(),
  /** One line in the member's language ("Create field “Renewal date” on Contracts"). */
  summary: z.string(),
  kind: z.enum(['write', 'danger']),
  undo: ChangeSetUndoKind,
  /** Why the step cannot be undone (irreversible steps only). */
  undoNote: z.string().optional(),
  /** What the step changes: the tool's dry run (typed changes, or a catalog preview). */
  preview: z.unknown().optional(),
  /** The step's dry run could not run yet: it depends on what earlier steps create. */
  deferred: z.boolean().optional(),
  warnings: z.array(z.string()).optional(),
  input: z.unknown(),
  /** Records the step writes (budget). */
  records: z.number().int().nonnegative().optional(),
  /** AI budget tokens the step spends (generated images). */
  tokens: z.number().int().nonnegative().optional(),
});
export type ChangeSetStepDTO = z.infer<typeof ChangeSetStepDTO>;

/** Pictures a step generates (image.generate, decks and pages with image prompts): what is asked and what it costs. */
const MediaBlockShape = {
  images: z.array(
    z.object({
      prompt: z.string(),
      /** Where it goes ("Slide 3", "Page header"). */
      place: z.string().optional(),
      style: z.string().optional(),
      size: z.string(),
      quality: z.string(),
      transparent: z.boolean(),
      /** Follows the brand kit (palette and mood written into the brief). */
      brand: z.boolean(),
      /** A variation or an edit of a library image. */
      from: z.string().optional(),
    }),
  ),
  /** Estimated AI budget tokens of all pictures of the step. */
  tokens: z.number().int().nonnegative(),
  /** The model that paints them ("OpenAI · gpt-image-1"), when one is configured. */
  model: z.string().optional(),
  local: z.boolean().optional(),
  /** What is left of the budget this month (the smaller of workspace and member), when budgets are set. */
  budgetLeft: z.number().int().nonnegative().optional(),
};
export const StepMedia = z.object(MediaBlockShape);
export type StepMedia = z.infer<typeof StepMedia>;

/**
 * What a step writes that a member must be able to read before approving: the e-mail that goes out, the
 * text of a page. A tool's dry run returns it as `preview.content`; the plan card shows it in full.
 */
export const StepContent = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('mail'),
    /** Recipients as plain addresses ("Mara Klein <m.klein@example.com>"). */
    to: z.array(z.string()),
    cc: z.array(z.string()).default([]),
    subject: z.string(),
    body: z.string(),
    /** Goes out at once and cannot be taken back (`mail.send`), or only lands in the composer. */
    sends: z.boolean(),
  }),
  z.object({
    kind: z.literal('document'),
    title: z.string(),
    sections: z.array(
      z.object({
        heading: z.string().optional(),
        text: z.string().optional(),
        bullets: z.array(z.string()).optional(),
      }),
    ),
    /** Pictures the page asks for. */
    media: StepMedia.optional(),
  }),
  z.object({ kind: z.literal('media'), ...MediaBlockShape }),
]);
export type StepContent = z.infer<typeof StepContent>;

export const ChangeSetResultDTO = z.object({
  id: z.string(),
  ok: z.boolean(),
  output: z.unknown().optional(),
  error: z.string().optional(),
  ms: z.number().int().nonnegative(),
});
export type ChangeSetResultDTO = z.infer<typeof ChangeSetResultDTO>;

export const ChangeSetBudgetDTO = z.object({
  steps: z.number().int().nonnegative(),
  maxSteps: z.number().int().positive(),
  records: z.number().int().nonnegative(),
  maxRecords: z.number().int().positive(),
});
export type ChangeSetBudgetDTO = z.infer<typeof ChangeSetBudgetDTO>;

/** Work somebody added after a change set to what its undo takes away: hidden (archived), never deleted. */
export const ChangeSetUndoImpact = z.object({
  kind: z.enum(['object', 'field']),
  /** The object's or field's name as the member knows it. */
  label: z.string(),
  /** Records typed into the object, or records holding a value in the field, after the change set. */
  records: z.number().int().nonnegative(),
});
export type ChangeSetUndoImpact = z.infer<typeof ChangeSetUndoImpact>;

export const ChangeSetUndoReport = z.object({
  steps: z.array(
    z.object({ id: z.string(), status: z.enum(['reverted', 'skipped', 'failed']), note: z.string().optional() }),
  ),
  records: z
    .object({
      reverted: z.number().int(),
      /** Records that were changed by somebody else since and stay as they are. */
      failed: z.number().int(),
      /** Records that already had their earlier value (nothing to take back). */
      unchanged: z.number().int().optional(),
    })
    .optional(),
  /** What the member confirmed to hide with the undo: records added after the change set. */
  hidden: z.array(ChangeSetUndoImpact).optional(),
});
export type ChangeSetUndoReport = z.infer<typeof ChangeSetUndoReport>;

export const ChangeSetDTO = z.object({
  id: z.uuid(),
  memberId: z.uuid().nullable(),
  memberName: z.string().nullable(),
  origin: ChangeSetOrigin,
  /** "AI · gpt-5", the MCP client or API key name. */
  sourceLabel: z.string().nullable(),
  threadId: z.string().nullable(),
  runId: z.uuid().nullable(),
  title: z.string(),
  summary: z.string(),
  status: ChangeSetStatus,
  steps: z.array(ChangeSetStepDTO),
  results: z.array(ChangeSetResultDTO),
  budget: ChangeSetBudgetDTO,
  error: z.string().nullable(),
  /** The caller may undo it now (applied, every step reversible, nothing later touched the same things). */
  undoable: z.boolean(),
  /** Why undo is not offered (a later change set touches the same things, a step is irreversible …). */
  undoBlocked: z.string().nullable(),
  /** Undo is possible, but would hide work added after the change set: ask first ("Undo anyway?"). */
  undoWarning: z.string().nullable().default(null),
  undoImpact: z.array(ChangeSetUndoImpact).default([]),
  undoReport: ChangeSetUndoReport.nullable(),
  /** The caller may approve or reject it (pending, and holds the rights of every step). */
  decidable: z.boolean(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
  appliedAt: z.string().nullable(),
  undoneAt: z.string().nullable(),
});
export type ChangeSetDTO = z.infer<typeof ChangeSetDTO>;

/** The input of the `plan.apply` tool and of queued MCP plans. */
export const PlanStepInput = z
  .object({
    tool: z.string().regex(/^[a-z][a-z0-9._-]{0,59}$/, 'A tool name such as field.create'),
    input: z.record(z.string(), z.unknown()).default({}),
    /** Optional label for the member ("Add the Priority field"); default: the tool's own summary. */
    title: z.string().trim().min(1).max(160).optional(),
  })
  .strict();
export type PlanStepInput = z.infer<typeof PlanStepInput>;

export const PlanApplyInput = z
  .object({
    title: z.string().trim().min(1).max(200).describe('What the whole change set achieves, e.g. "Set up contract management"'),
    summary: z.string().trim().max(2000).default('').describe('Optional: why, and what the member should know'),
    steps: z.array(PlanStepInput).min(1).max(60),
  })
  .strict();
export type PlanApplyInput = z.infer<typeof PlanApplyInput>;

const IdParams = z.object({ id: z.uuid() });

/**
 * Where a result of the agent's tools lives in the app. One place, so the result chips of the plan card
 * and the golden test that checks them against the web routes (`apps/web/test/agent-links.test.ts`) cannot
 * drift: an href is added here only together with a route.
 */
export const agentHref = {
  object: (key: string) => `/o/${encodeURIComponent(key)}`,
  view: (object: string, view: string) =>
    `/o/${encodeURIComponent(object)}?view=${encodeURIComponent(view)}`,
  dashboard: (key: string) => `/dashboards/${encodeURIComponent(key)}`,
  automation: (id: string) => `/automations/${encodeURIComponent(id)}`,
  page: (id: string) => `/pages/${encodeURIComponent(id)}`,
  deck: (id: string) => `/decks/${encodeURIComponent(id)}`,
  document: (id: string) => `/documents/${encodeURIComponent(id)}`,
  task: (id: string) => `/inbox?task=${encodeURIComponent(id)}`,
  /** The builder of a custom component. */
  component: (id: string) => `/components/${encodeURIComponent(id)}`,
  /** The designer of an object's record page, on the layout that was created. */
  layout: (object: string, layoutId: string) =>
    `/settings/objects/${encodeURIComponent(object)}/layout?variant=${encodeURIComponent(layoutId)}`,
} as const;

export const listChangeSets = defineContract({
  method: 'GET',
  path: '/ai/change-sets',
  query: z.object({
    scope: z.enum(['me', 'pending', 'workspace']).default('me'),
    limit: q.int().min(1).max(100).default(30),
  }),
  response: z.object({ items: z.array(ChangeSetDTO), pending: z.number().int().nonnegative() }),
  summary: 'AI change sets: own, waiting for approval (MCP queue) or the whole workspace (admins)',
});
export const getChangeSet = defineContract({
  method: 'GET',
  path: '/ai/change-sets/:id',
  params: IdParams,
  response: ChangeSetDTO,
  summary: 'One AI change set with its plan, results and undo state',
});
export const approveChangeSet = defineContract({
  method: 'POST',
  path: '/ai/change-sets/:id/approve',
  params: IdParams,
  body: z.object({}).strict(),
  response: ChangeSetDTO,
  summary: 'Approve a queued change set: apply every step, all or nothing',
});
export const rejectChangeSet = defineContract({
  method: 'POST',
  path: '/ai/change-sets/:id/reject',
  params: IdParams,
  body: z.object({ reason: z.string().trim().max(500).optional() }).strict(),
  response: ChangeSetDTO,
  summary: 'Reject a queued change set',
});
/** What an administrator may tune about changes by AI (Settings › AI › Agent). */
export const AgentSettingsDTO = z.object({
  /** `read_only`: the agent may read, explain and propose, but no change set applies. */
  writes: z.enum(['allowed', 'read_only']),
  maxSteps: z.number().int().min(1).max(AI_CHANGE_SET_LIMITS.maxSteps),
  maxRecords: z.number().int().min(1).max(AI_CHANGE_SET_LIMITS.maxRecords),
  perHour: z.number().int().min(1).max(AI_CHANGE_SET_LIMITS.perHour),
  /** The platform ceilings the limits cannot exceed. */
  caps: z.object({
    maxSteps: z.number().int(),
    maxRecords: z.number().int(),
    perHour: z.number().int(),
  }),
  canManage: z.boolean(),
  /** Tools the agent has for the caller, by kind. */
  tools: z.object({
    read: z.number().int(),
    propose: z.number().int(),
    write: z.number().int(),
    danger: z.number().int(),
  }),
  /**
   * Names of the tools that change something and that the caller may use (the Copilot offers starters
   * only for what the member can do; an empty list in `read_only` mode is not implied: see `writes`).
   */
  writeTools: z.array(z.string()).default([]),
  /**
   * How many of those tools external agents (MCP) get: the same registry without the tools that only
   * exist for the in-app cards (undoing a change set, editors). Their changes are queued for approval.
   */
  mcpTools: z.number().int().default(0),
});
export type AgentSettingsDTO = z.infer<typeof AgentSettingsDTO>;

export const getAgentSettings = defineContract({
  method: 'GET',
  path: '/ai/agent',
  response: AgentSettingsDTO,
  summary: 'Limits of the platform agent and how many tools the caller has',
});
export const updateAgentSettings = defineContract({
  method: 'PUT',
  path: '/ai/agent',
  body: z
    .object({
      writes: z.enum(['allowed', 'read_only']).optional(),
      maxSteps: z.number().int().min(1).max(AI_CHANGE_SET_LIMITS.maxSteps).optional(),
      maxRecords: z.number().int().min(1).max(AI_CHANGE_SET_LIMITS.maxRecords).optional(),
      perHour: z.number().int().min(1).max(AI_CHANGE_SET_LIMITS.perHour).optional(),
    })
    .strict(),
  response: AgentSettingsDTO,
  permission: 'workspace.admin',
  summary: 'Change the limits of the platform agent (administrators)',
});

export const undoChangeSet = defineContract({
  method: 'POST',
  path: '/ai/change-sets/:id/undo',
  params: IdParams,
  body: z
    .object({
      /** The member read `undoWarning` and wants the undo anyway (without it a set with added work is refused). */
      confirm: z.boolean().optional(),
    })
    .strict(),
  response: ChangeSetDTO,
  summary: 'Undo a whole applied change set in one step',
});

/**
 * A planned change shown in the app before it is applied (the Copilot's "Preview in app"): the server
 * replays the steps in a transaction it never commits and answers the member's reads that carry the
 * `x-dataroom-preview: <id>` header from inside it. Writes are refused while the header is sent.
 */
export const AiPreviewDTO = z.object({
  id: z.uuid(),
  expiresAt: z.string(),
  /** Where to look: what the steps made, in plan order (objects, views, dashboards, …). */
  links: z.array(z.object({ tool: z.string(), label: z.string(), href: z.string() })),
  /** Titles of steps the preview does not replay (mail, generated images, automations). */
  skipped: z.array(z.string()),
  /** Fields the plan adds to objects that exist already (their lists highlight these columns). */
  fields: z.array(z.object({ object: z.string(), key: z.string(), label: z.string() })),
});
export type AiPreviewDTO = z.infer<typeof AiPreviewDTO>;
export const openAiPreview = defineContract({
  method: 'POST',
  path: '/ai/previews',
  body: z
    .object({
      /** The write tool waiting for approval (`plan.apply`, `blueprint.build`, `object.create`, …). */
      tool: z.string().min(1).max(80),
      input: z.unknown(),
    })
    .strict(),
  response: AiPreviewDTO,
  summary: 'Preview a planned change in the app without applying it',
});
export const closeAiPreview = defineContract({
  method: 'DELETE',
  path: '/ai/previews/:id',
  params: IdParams,
  response: z.void(),
  summary: 'End a preview (its transaction is rolled back)',
});
